import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { postDataAPI, getDataAPI } from '../../utils/fetchData';
import Modal from '../../components/Modal';
import { useModal } from '../../hooks/useModal';
import CustomTimePicker from '../../components/Calendar/CustomTimePicker';

const AddGroundTransport = () => {
  const navigate = useNavigate();
  const { showId } = useParams();
  const { modalConfig, showModal, closeModal } = useModal();
  const [loading, setLoading] = useState(false);
  const [showData, setShowData] = useState(null);
  const [loadingShow, setLoadingShow] = useState(true);

  // Form state based on the modal structure
  const [formData, setFormData] = useState({
    pickupTime: '',
    pickupLocation: '',
    dropoffLocation: 'To Venue',
    vehicleType: '',
    driverName: '',
    driverPhone: '',
    company: '',
    confirmationNumber: '',
    status: 'planned',
    notes: '',
    transportType: 'driver',
  });

  useEffect(() => {
    if (showId) {
      loadShowData();
    }
  }, [showId]);

  const loadShowData = async () => {
    try {
      setLoadingShow(true);
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await getDataAPI(`user/tm/shows/${showId}`, token);
      
      if (response.data.status === 200 && response.data.data) {
        setShowData(response.data.data);
      }
    } catch (error) {
      console.error('Error loading show data:', error);
      showModal('Error', 'Failed to load show details.', 'error');
    } finally {
      setLoadingShow(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.pickupTime) {
      showModal('Required Field', 'Please enter a pickup time.', 'warning');
      return;
    }
    if (!formData.pickupLocation) {
      showModal('Required Field', 'Please enter a pickup location.', 'warning');
      return;
    }
    if (!formData.dropoffLocation) {
      showModal('Required Field', 'Please enter a destination.', 'warning');
      return;
    }

    try {
      setLoading(true);
      const token = JSON.parse(localStorage.getItem('dizeeToken'));

      // Combine show date with pickup time
      const showDate = new Date(showData.showDate || showData.show_date);
      const [hours, minutes] = formData.pickupTime.split(':');
      const pickupDateTime = new Date(showDate);
      pickupDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      const transportData = {
        tourShowId: showData._id,
        transportType: formData.transportType,
        status: formData.status,
        provider: formData.company || undefined,
        vehicleInfo: formData.vehicleType ? {
          type: formData.vehicleType,
        } : undefined,
        driver: (formData.driverName || formData.driverPhone) ? {
          name: formData.driverName,
          phone: formData.driverPhone,
        } : undefined,
        pickup: {
          location: formData.pickupLocation,
          time: pickupDateTime,
        },
        dropoff: {
          location: formData.dropoffLocation,
        },
        passengers: 1,
        confirmationNumber: formData.confirmationNumber || undefined,
        notes: formData.notes || '',
      };

      console.log('Creating ground transport:', transportData);

      const response = await postDataAPI('user/tm/ground-transport', transportData, token);

      if (response.data.status === 200 || response.data.status === 201) {
        showModal('Transport Added', 'Ground transport has been added successfully!', 'success');
        setTimeout(() => {
          navigate(-1);
        }, 1500);
      } else {
        showModal('Error', response.data.message || 'Failed to add transport.', 'error');
      }
    } catch (error) {
      console.error('Error adding transport:', error);
      showModal('Error', error.response?.data?.message || 'Failed to add transport. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loadingShow) {
    return (
      <LayoutHeader>
        <div className="flex justify-center w-full">
          <div className="w-[390px] h-[80vh] flex flex-col items-center justify-center">
            <p className="text-white text-[12px]">Loading show details...</p>
          </div>
        </div>
      </LayoutHeader>
    );
  }

  if (!showData) {
    return (
      <LayoutHeader>
        <div className="flex justify-center w-full">
          <div className="w-[390px] h-[80vh] flex flex-col items-center justify-center">
            <p className="text-white text-[12px]">Show not found</p>
            <button
              onClick={() => navigate(-1)}
              className="mt-4 text-[#FF9500] text-[12px] hover:opacity-70"
            >
              Go back
            </button>
          </div>
        </div>
      </LayoutHeader>
    );
  }

  const basicFields = [
    { key: 'pickupTime', label: 'Pickup Time', type: 'time', required: true },
    { key: 'pickupLocation', label: 'Pickup Location', placeholder: 'e.g., Hilton Downtown', required: true },
    { key: 'dropoffLocation', label: 'Destination', placeholder: 'e.g., To Venue', required: true },
    { key: 'vehicleType', label: 'Vehicle', placeholder: 'e.g., Sprinter (SUV)' },
    { key: 'driverName', label: 'Drop Contact', placeholder: 'e.g., Jay' },
    { key: 'driverPhone', label: 'Contact Phone', type: 'tel', placeholder: 'e.g., (555) 123-4567' },
    { key: 'company', label: 'Company', placeholder: 'e.g., Westline Transit' },
    { key: 'confirmationNumber', label: 'Confirmation #', placeholder: 'e.g., WVTX13579' },
    { key: 'status', label: 'Status', type: 'select', options: [
      { value: 'planned', label: 'Planned' },
      { value: 'booked', label: 'Booked' },
      { value: 'confirmed', label: 'Confirmed' },
      { value: 'completed', label: 'Completed' },
      { value: 'cancelled', label: 'Cancelled' }
    ]},
    { key: 'notes', label: 'Additional Notes', type: 'textarea', placeholder: 'e.g., Black Sprinter van' }
  ];

  const renderField = (field) => {
    const value = formData[field.key] || '';

    if (field.type === 'time') {
      return (
        <CustomTimePicker
          value={value}
          onChange={(time) => handleChange(field.key, time)}
          label={`${field.label}${field.required ? ' *' : ''}`}
          placeholder={field.placeholder || 'Select time...'}
          theme="tour-manager"
          required={field.required}
        />
      );
    }

    if (field.type === 'select') {
      return (
        <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF9500] hover:bg-[rgba(255,149,0,0.05)] transition-all">
          <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
            {field.label}
            {field.required && <span className="text-[#FF9500] ml-1">*</span>}
          </label>
          <select
            value={value}
            onChange={(e) => handleChange(field.key, e.target.value)}
            className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
          >
            {field.options.map(option => (
              <option key={option.value} value={option.value} className="bg-black">
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (field.type === 'textarea') {
      return (
        <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF9500] hover:bg-[rgba(255,149,0,0.05)] transition-all">
          <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
            {field.label}
            {field.required && <span className="text-[#FF9500] ml-1">*</span>}
          </label>
          <textarea
            value={value}
            onChange={(e) => handleChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            rows={3}
            className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] resize-none"
          />
        </div>
      );
    }

    return (
      <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF9500] hover:bg-[rgba(255,149,0,0.05)] transition-all">
        <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
          {field.label}
          {field.required && <span className="text-[#FF9500] ml-1">*</span>}
        </label>
        <input
          type={field.type || 'text'}
          value={value}
          onChange={(e) => handleChange(field.key, e.target.value)}
          placeholder={field.placeholder}
          className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
        />
      </div>
    );
  };

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Add Ground Transport</span>
            <div className="flex items-center gap-4">
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={handleSubmit}
              >
                Save
              </span>
              
              <span className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" onClick={() => navigate(-1)}>Go back</span>
            </div>
          </div>
          
          {/* Show Info */}
          <div className="px-4 mb-6 w-full">
            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 mb-4">
              <div className="text-white text-[12px] font-medium mb-1">
                {showData.venue?.name || showData.venue}
              </div>
              <div className="text-[rgba(255,255,255,0.7)] text-[10px]">
                {showData.city || showData.venue?.city}, {showData.state || showData.venue?.state}
              </div>
              <div className="text-[rgba(255,255,255,0.5)] text-[10px] mt-1">
                {new Date(showData.showDate || showData.show_date).toLocaleDateString('en-US', { 
                  weekday: 'short', 
                  month: 'short', 
                  day: 'numeric', 
                  year: 'numeric' 
                })}
              </div>
            </div>
            <div className="text-[10px] text-[rgba(255,255,255,0.7)] text-left">
              Fill in the required fields to add ground transport for this show.
            </div>
          </div>

          <div className="w-full px-4 flex-1 overflow-y-auto">
            <div className="flex flex-col">
              {/* Fields */}
              <div className="space-y-4 mb-6">
                {basicFields.map(renderField)}
              </div>

              {/* Save Button */}
              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF9500] hover:bg-[rgba(255,149,0,0.05)] transition-all">
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full bg-[#FF9500] text-black text-[12px] font-medium py-3 rounded-lg hover:bg-[#FF8800] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2"></div>
                      Adding Transport...
                    </>
                  ) : (
                    'Add Ground Transport'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Modal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        onConfirm={modalConfig.onConfirm}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
        showCancel={modalConfig.showCancel}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
      />
    </LayoutHeader>
  );
};

export default AddGroundTransport;
